import config from 'Cloud/config';
import { extInfo } from 'lib/extInfo';
import { IS_MOBILE_BROWSER } from 'reactApp/appHelpers/configHelpers';
import { familyAlmbumPublicLink } from 'reactApp/appHelpers/featuresHelpers/features/familyFunctions';
import { isHiDPI } from 'reactApp/appHelpers/settingsHelpers';
import { CUSTOM_ALBUMS_ID } from 'reactApp/modules/albums/albums.constants';
import type { FamilyContentAlbum, FamilyContentFolderItemBase } from 'reactApp/modules/familyContent/familyContent.types';
import { getExtension } from 'reactApp/modules/file/helpers/getExtention';
import { getItemNameWithoutExt, getParent, normalizeId, normalizePublicApiInfo } from 'reactApp/modules/file/utils';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { thumbsSize, UrlBuilder } from 'reactApp/modules/urlBuilder/UrlBuilder';
import type { IPublicInfo } from 'reactApp/types/Tree';
import { normalizeMtime } from 'reactApp/utils/tree.helpers';

const urlBuilder = new UrlBuilder();

const getBuilderParams = (url: string) => {
    if (!url) {
        return undefined;
    }

    // Path приходит заэнкоженным
    const path = decodeURIComponent(url);
    const ext = getExtension(url);
    const { kind, subKind } = extInfo.get(ext);

    return {
        ext,
        size: config.get('ITEM_WITHOUT_HASH_MAX_SIZE'),
        path,
        kind,
        subKind,
    };
};

export const normalizeBaseFamilyFoldersItem = (feedItem) => {
    const parentFolder = feedItem.home ? normalizeId(getParent(feedItem.home)) : '/';
    const ext = getExtension(feedItem);
    const nameWithoutExt = getItemNameWithoutExt(feedItem.name, ext);
    const { kind, subKind } = extInfo.get(ext);

    const normalizedItem: FamilyContentFolderItemBase = {
        isReadOnly: false,
        id: feedItem.home,
        isFolder: true,
        parent: parentFolder,
        ext,
        nameWithoutExt,
        name: feedItem.name,
        home: feedItem.home || '',
        size: feedItem.size,
        kind: 'family',
        subKind,
        mtime: normalizeMtime(feedItem.mtime),
        virusScan: feedItem.virus_scan,
        hash: feedItem.hash,
        uflr: 'uflr' in feedItem ? feedItem.uflr : undefined,
        isInFavorites: 'favorite' in feedItem && (feedItem.favorite === 1 || feedItem.favorite === true),
        weblink: '',
        url: urlBuilder.getUrls({
            ext,
            isPublic: false,
            id: feedItem.home,
            weblink: '',
            size: feedItem.size,
            kind,
            subKind,
            isStock: false,
            name: feedItem.name,
            path: feedItem.home,
        }),
        thumbnails: urlBuilder.getThumb({
            hash: feedItem.hash,
            ext,
            id: feedItem.home,
            size: feedItem.size,
            kind,
            name: feedItem.name,
            path: feedItem.home,
            isPublic: false,
            isStock: false,
            dwl_token: null,
            weblink: '',
        }),
    };

    if ('public' in feedItem && feedItem.public) {
        return {
            ...normalizedItem,
            ...normalizePublicApiInfo(feedItem),
        };
    }

    return normalizedItem;
};

const getPublicFamilyPreviewUrl: (props: { base_url?: string; path?: string }) => Record<string, string> | undefined = (props) => {
    if (!props) {
        return;
    }

    const { base_url, path } = props;

    const thumbs = Object.entries(thumbsSize).reduce((acc, item) => {
        const [key, value] = item;

        acc[key] = `${base_url}${value.sizeByPath}/${path}`;

        return acc;
    }, {});

    const mobileSize = isHiDPI ? '160x120' : '52x52';
    const size = IS_MOBILE_BROWSER ? mobileSize : '72x96';

    thumbs['pic'] = thumbs[size];

    return thumbs;
};

export const normalizeFamilyContentAlbumItem = (item): FamilyContentAlbum => {
    const isOwner = item?.owner_email === config.get('user').email;
    const thumbUrl = item.album_cover_thumb_url?.path;
    const urlBuilderParams = getBuilderParams(thumbUrl);
    const publicThumbs = getPublicFamilyPreviewUrl(item.weblink_thumb);
    const thumbs = thumbUrl ? urlBuilder.getThumb(urlBuilderParams) : undefined;

    if (isOwner) {
        delete item.public;
    }

    const weblinkInfo = normalizePublicApiInfo(item) as IPublicInfo;

    return {
        ...weblinkInfo,
        cursor: item.cursor,
        id: item.id,
        kind: 'album',
        name: item.title,
        hasMoreToLoad: Boolean(item.cursor),
        parent: CUSTOM_ALBUMS_ID,
        flags: { isMedia: true },
        isFolder: true,
        storage: EStorageType.albums,
        type: 'album',
        childs: [],
        thumbnails: !isOwner && familyAlmbumPublicLink ? publicThumbs : thumbs,
        photosCount: item.photos_count,
        href: weblinkInfo.weblink && familyAlmbumPublicLink ? `/album/${weblinkInfo.weblink}` : `/albums/custom/${item.id}`,
        isOpenBlank: weblinkInfo.weblink && familyAlmbumPublicLink,
    };
};
